import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { confirmedMessage, reportMessageSendFailure } from '../../util/messageSending';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { getRequestedTransactionId } from '../../util/messagesPageHelpers';
import { getValidInboxSort } from '../../util/urlHelpers';
import {
  guardedTransaction,
  processTransactionSmsNotifications,
  sendMarketplaceMessage,
} from '../../util/api';
import { transitions as enquiryTransitions } from '../../transactions/transactionProcessInquiry';
import { transitions as purchaseTransitions } from '../../transactions/transactionProcessPurchase';
import { NO_IMAGE } from '../../util/constants';
import { OFFER_TRANSITIONS, filterMessageConversations } from '../../util/offerHelpers';
import {
  isBlockedTransactionId,
  sanitizeTransactionQueryResponse,
} from '../../util/transactionVisibility';
import { createBlockedNonOrderMessagingError } from '../../util/nonOrderMessaging';
import {
  createParticipantUnavailableError,
  filterMessagesBySenderAvailability,
  getUnavailableTransactionParticipant,
  splitTransactionsByParticipantAvailability,
  unavailableMessageParticipantLogData,
  unavailableParticipantLogData,
} from '../../util/participantAvailability';
import { toCanonicalTransitionName } from '../../transactions/transitionAliases';

const TRANSACTION_PAGE_SIZE = 25;
const MESSAGE_PAGE_SIZE = 100;
const TAB_ORDER = 'order';
const TAB_ENQUIRY = 'enquiry';

const triggerTransactionSmsNotifications = ({ transactionId, source, transition }) => {
  if (!transactionId) {
    return;
  }

  processTransactionSmsNotifications({ transactionId, source, transition }).catch(e => {
    log.warn(e, 'transaction-sms-direct-trigger-failed', {
      source,
      transactionId: transactionId?.uuid || transactionId,
      transition,
    });
  });
};

const USER_FIELDS = [
  'profile.displayName',
  'profile.abbreviatedName',
  'profile.publicData',
  'deleted',
  'banned',
  'state',
  'disabled',
];

const commonApiQueryParams = {
  include: [
    'listing',
    'provider',
    'provider.profileImage',
    'customer',
    'customer.profileImage',
    'booking',
    'messages',
    'messages.sender',
    'messages.sender.profileImage',
    'listing.images',
  ],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'transitions',
    'payinTotal',
    'payoutTotal',
    'lineItems',
    'protectedData',
  ],
  'fields.user': USER_FIELDS,
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

const transactionOnlyApiQueryParams = {
  ...commonApiQueryParams,
  include: commonApiQueryParams.include.filter(
    includeParam => !includeParam.startsWith('messages')
  ),
};

const enquiryOrOfferTransitions = [
  enquiryTransitions.INQUIRE_WITHOUT_PAYMENT,
  purchaseTransitions.INQUIRE,
  'transition/enquire',
];

const orderMessageTransitions = Object.values(purchaseTransitions).filter(
  transition =>
    transition !== purchaseTransitions.INQUIRE && !OFFER_TRANSITIONS.includes(transition)
);

const withTransactions = (response, transactions) => ({
  ...response,
  data: {
    ...response?.data,
    data: transactions,
  },
});

const getLastMessageTimestamp = tx => {
  const txMessages = tx?.messages;
  const lastMessage =
    Array.isArray(txMessages) && txMessages.length > 0 ? txMessages[txMessages.length - 1] : null;

  return new Date(lastMessage?.attributes?.createdAt || 0).getTime();
};

const getLastTransitionedAtTimestamp = tx =>
  new Date(tx?.attributes?.lastTransitionedAt || 0).getTime();

const sortedTransactions = txs =>
  [...(txs || [])].sort((a, b) => {
    const lastMessageDelta = getLastMessageTimestamp(b) - getLastMessageTimestamp(a);
    if (lastMessageDelta !== 0) {
      return lastMessageDelta;
    }

    return getLastTransitionedAtTimestamp(b) - getLastTransitionedAtTimestamp(a);
  });

const mergeUniqueById = entities => {
  const entityMap = entities.reduce((acc, entity) => {
    const id = entity?.id?.uuid;
    if (!id) {
      return acc;
    }
    acc.set(id, entity);
    return acc;
  }, new Map());

  return Array.from(entityMap.values());
};

const mergeMessageArrays = (current, incoming) => {
  const combined = mergeUniqueById([...(current || []), ...(incoming || [])]);
  return combined.sort((a, b) => {
    const aDate = new Date(a?.attributes?.createdAt || 0).getTime();
    const bDate = new Date(b?.attributes?.createdAt || 0).getTime();
    return aDate - bDate;
  });
};

const logHiddenUnavailableTransactions = (hiddenTransactions, code) => {
  hiddenTransactions.forEach(({ transaction, unavailableParticipant }) => {
    log.warn(
      createParticipantUnavailableError(),
      code,
      unavailableParticipantLogData(transaction, unavailableParticipant)
    );
  });
};

const logHiddenUnavailableMessages = (hiddenMessages, txId) => {
  hiddenMessages.forEach(({ message, unavailableParticipant, reason }) => {
    if (reason === 'pending-review') {
      log.warn(new Error('Pending review message hidden.'), 'message-hidden-pending-review', {
        txId: txId?.uuid || txId,
        messageId: message?.id?.uuid || message?.id || null,
      });
      return;
    }

    log.warn(
      createParticipantUnavailableError(),
      'message-hidden-unavailable-participant',
      unavailableMessageParticipantLogData(message, unavailableParticipant, txId)
    );
  });
};

const getLoadedTransaction = (state, txId) => {
  const txUuid = txId?.uuid || txId;
  const transactionRefs = state?.MessagesPage?.transactionRefs || [];
  const transactionRef = transactionRefs.find(ref => (ref?.id?.uuid || ref?.id) === txUuid);

  return transactionRef ? getMarketplaceEntities(state, [transactionRef])[0] : null;
};

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/MessagesPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/MessagesPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/MessagesPage/FETCH_ORDERS_OR_SALES_ERROR';
export const SET_TRANSACTION_PAGE = 'app/MessagesPage/SET_TRANSACTION_PAGE';

export const FETCH_MESSAGES_REQUEST = 'app/MessagesPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/MessagesPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/MessagesPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/MessagesPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/MessagesPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/MessagesPage/SEND_MESSAGE_ERROR';

export const UPDATE_OFFER_PROGRESS = 'app/MessagesPage/UPDATE_OFFER_PROGRESS';
export const UPDATE_OFFER_SUCCESS = 'app/MessagesPage/UPDATE_OFFER_SUCCESS';
export const UPDATE_OFFER_ERROR = 'app/MessagesPage/UPDATE_OFFER_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  transactionPageFetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  transactionPageRefs: {},
  loadedTransactionPages: [],
  currentTransactionPage: 1,
  sendMessageInProgress: false,
  sendMessageError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  messages: [],
  messageTransactionId: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  updateOfferInProgress: false,
  updateOfferSuccess: false,
  updateOfferError: null,
};

export default function messagesPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case UPDATE_OFFER_PROGRESS:
      return {
        ...state,
        updateOfferInProgress: true,
        updateOfferSuccess: false,
        updateOfferError: null,
      };
    case UPDATE_OFFER_SUCCESS:
      return { ...state, updateOfferSuccess: true, updateOfferInProgress: false };
    case UPDATE_OFFER_ERROR:
      return { ...state, updateOfferError: payload || true, updateOfferInProgress: false };

    case FETCH_ORDERS_OR_SALES_REQUEST: {
      const { isPageChange = false, reset = false } = payload || {};
      return {
        ...state,
        fetchInProgress: !isPageChange,
        transactionPageFetchInProgress: isPageChange,
        fetchOrdersOrSalesError: null,
        ...(reset
          ? {
              pagination: null,
              transactionRefs: [],
              transactionPageRefs: {},
              loadedTransactionPages: [],
              currentTransactionPage: 1,
            }
          : {}),
      };
    }
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const page = Number(payload.page || payload.pagination?.page || 1);
      const transactions = sortedTransactions(payload.transactions || []);
      const pageRefs = entityRefs(transactions);

      return {
        ...state,
        fetchInProgress: false,
        transactionPageFetchInProgress: false,
        transactionRefs: pageRefs,
        transactionPageRefs: {
          ...state.transactionPageRefs,
          [page]: pageRefs,
        },
        loadedTransactionPages: state.loadedTransactionPages.includes(page)
          ? state.loadedTransactionPages
          : [...state.loadedTransactionPages, page].sort((a, b) => a - b),
        currentTransactionPage: page,
        pagination: payload.pagination ? { ...payload.pagination, page } : null,
      };
    }
    case SET_TRANSACTION_PAGE: {
      const page = Number(payload?.page || 1);
      const pageRefs = state.transactionPageRefs[page] || [];

      return {
        ...state,
        currentTransactionPage: page,
        transactionRefs: pageRefs,
        pagination: state.pagination ? { ...state.pagination, page } : state.pagination,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        fetchInProgress: false,
        transactionPageFetchInProgress: false,
        fetchOrdersOrSalesError: payload,
      };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const isSameThread =
        state.messageTransactionId &&
        payload.transactionId &&
        state.messageTransactionId === payload.transactionId;
      const existingMessages = isSameThread ? state.messages : [];
      const mergedMessages = mergeMessageArrays(existingMessages, payload.messages || []);

      return {
        ...state,
        fetchMessagesInProgress: false,
        fetchMessagesError: null,
        messages: mergedMessages,
        messageTransactionId: payload.transactionId,
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched: isSameThread
          ? Math.max(state.oldestMessagePageFetched, payload.page)
          : payload.page,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return {
        ...state,
        sendMessageInProgress: false,
        messageTransactionId: payload.transactionId,
        messages: mergeMessageArrays(
          state.messageTransactionId === payload.transactionId ? state.messages : [],
          [payload.message]
        ),
      };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const selectUpdateOfferInProgress = state => state.MessagesPage.updateOfferInProgress;
export const selectUpdateOfferSuccess = state => state.MessagesPage.updateOfferSuccess;
export const selectUpdateOfferError = state => state.MessagesPage.updateOfferError;

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = payload => ({ type: FETCH_ORDERS_OR_SALES_REQUEST, payload });
const fetchOrdersOrSalesSuccess = (transactions, pagination, page) => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: { transactions, pagination, page },
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});
const setTransactionPage = page => ({ type: SET_TRANSACTION_PAGE, payload: { page } });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination, transactionId) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, transactionId, ...pagination },
});
const fetchMessagesError = e => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = (message, transactionId) => ({
  type: SEND_MESSAGE_SUCCESS,
  payload: { message, transactionId },
});
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const updateOfferProgress = () => ({ type: UPDATE_OFFER_PROGRESS });
const updateOfferSuccess = () => ({ type: UPDATE_OFFER_SUCCESS });
const updateOfferError = error => ({ type: UPDATE_OFFER_ERROR, payload: error, error: true });

// ================ Helpers ================ //

export const normalizeMessagesTab = tab => {
  if (tab === 'order' || tab === 'orders' || tab === 'all') {
    return TAB_ORDER;
  }

  if (tab === 'enquiry' || tab === 'sales') {
    return TAB_ENQUIRY;
  }

  return TAB_ENQUIRY;
};

const getTransitionsForTab = tab => {
  const normalizedTab = normalizeMessagesTab(tab);
  return normalizedTab === TAB_ORDER ? orderMessageTransitions : enquiryOrOfferTransitions;
};

const isOrderMessageTransaction = tx => {
  const lastTransition = toCanonicalTransitionName(tx?.attributes?.lastTransition);
  return orderMessageTransitions.includes(lastTransition);
};

const isEnquiryMessageTransaction = tx => {
  const lastTransition = toCanonicalTransitionName(tx?.attributes?.lastTransition);
  return enquiryOrOfferTransitions.map(toCanonicalTransitionName).includes(lastTransition);
};

const isMessageTransactionForTab = (tx, tab) => {
  const normalizedTab = normalizeMessagesTab(tab);
  return normalizedTab === TAB_ORDER
    ? isOrderMessageTransaction(tx)
    : isEnquiryMessageTransaction(tx);
};

const createTransactionQueryParams = ({ tab, page = 1 }) => ({
  lastTransitions: getTransitionsForTab(tab),
  ...commonApiQueryParams,
  page,
  perPage: TRANSACTION_PAGE_SIZE,
  ...getValidInboxSort('lastMessageAt'),
});

const queryTransactionsPage = async (dispatch, sdk, queryParams) => {
  const MAX_RETRIES = 2;
  const queryWithRetry = async retriesLeft => {
    try {
      return await sdk.transactions.query(queryParams);
    } catch (e) {
      if (retriesLeft > 0) {
        // Wait a bit and retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  const response = await queryWithRetry(MAX_RETRIES);
  const sanitizedResponse = sanitizeTransactionQueryResponse(response);
  const messageTransactions = filterMessageConversations(sanitizedResponse?.data?.data || []);
  let denormalizedTransactions = [];

  try {
    denormalizedTransactions = denormalisedResponseEntities(
      withTransactions(sanitizedResponse, messageTransactions)
    );
  } catch (e) {
    log.warn(e, 'messages-participant-denormalization-failed', {
      page: queryParams?.page || null,
    });
  }

  const { visibleTransactions, hiddenTransactions } = splitTransactionsByParticipantAvailability(
    messageTransactions,
    denormalizedTransactions,
    sanitizedResponse?.data?.included || []
  );
  logHiddenUnavailableTransactions(
    hiddenTransactions,
    'conversation-hidden-unavailable-participant'
  );
  const filteredResponse = withTransactions(sanitizedResponse, visibleTransactions);
  dispatch(addMarketplaceEntities(filteredResponse));

  return {
    transactions: visibleTransactions,
    hiddenTransactionIds: hiddenTransactions.map(({ transaction }) => transaction?.id?.uuid),
    pagination: filteredResponse?.data?.meta || {},
  };
};

const fetchTransactionById = async (dispatch, sdk, transactionId, tab) => {
  if (
    typeof sdk.transactions?.show !== 'function' ||
    !transactionId ||
    isBlockedTransactionId(transactionId)
  ) {
    return null;
  }

  const MAX_RETRIES = 2;
  const showWithRetry = async retriesLeft => {
    try {
      return await sdk.transactions.show(
        { id: transactionId },
        {
          expand: true,
          ...transactionOnlyApiQueryParams,
        }
      );
    } catch (e) {
      if (retriesLeft > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return showWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  const response = await showWithRetry(MAX_RETRIES);
  const transaction = denormalisedResponseEntities(response)[0] || null;
  const unavailableParticipant = getUnavailableTransactionParticipant(transaction);

  if (unavailableParticipant) {
    log.warn(
      createParticipantUnavailableError(),
      'message-thread-blocked-unavailable-participant',
      unavailableParticipantLogData(transaction, unavailableParticipant)
    );
    return null;
  }

  if (
    !transaction ||
    !filterMessageConversations([transaction]).length ||
    !isMessageTransactionForTab(transaction, tab)
  ) {
    if (transaction?.id) {
      log.warn(createBlockedNonOrderMessagingError(), 'non-order-message-thread-blocked', {
        txId: transaction?.id?.uuid || transactionId,
        tab,
      });
    }
    return null;
  }

  dispatch(addMarketplaceEntities(response));

  return transaction;
};

const fetchMessages = (txId, page) => (dispatch, getState, sdk) => {
  const txUuid = txId?.uuid || txId;
  const paging = { page, per_page: MESSAGE_PAGE_SIZE };
  dispatch(fetchMessagesRequest());

  const transaction = getLoadedTransaction(getState(), txId);
  const unavailableParticipant = getUnavailableTransactionParticipant(transaction);

  if (unavailableParticipant) {
    const blockedError = createParticipantUnavailableError();
    log.warn(
      blockedError,
      'messages-fetch-blocked-unavailable-participant',
      unavailableParticipantLogData(transaction, unavailableParticipant)
    );
    dispatch(fetchMessagesError(storableError(blockedError)));
    return Promise.resolve(null);
  }

  const MAX_RETRIES = 2;
  const queryMessagesWithRetry = async retriesLeft => {
    try {
      return await sdk.messages.query({
        transaction_id: txId,
        include: ['sender', 'sender.profileImage'],
        'fields.user': USER_FIELDS,
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
        ...paging,
      });
    } catch (e) {
      if (retriesLeft > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryMessagesWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  return queryMessagesWithRetry(MAX_RETRIES)
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { visibleMessages, hiddenMessages } = filterMessagesBySenderAvailability(messages);
      logHiddenUnavailableMessages(hiddenMessages, txId);

      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const totalItemsCount = Number(totalItems);
      const safeTotalItems = Number.isFinite(totalItemsCount)
        ? Math.max(visibleMessages.length, totalItemsCount - hiddenMessages.length)
        : visibleMessages.length;
      const pagination = { totalItems: safeTotalItems, totalPages, page: fetchedPage };
      const totalMessagesInStore = getState()?.MessagesPage?.totalMessages || 0;

      dispatch(fetchMessagesSuccess(visibleMessages, pagination, txUuid));

      if (page > 1 && totalItems > totalMessagesInStore) {
        dispatch(fetchMessages(txId, 1));
      }
    })
    .catch(e => {
      dispatch(fetchMessagesError(storableError(e)));
    });
};

const fetchTransactionPage = async ({
  dispatch,
  sdk,
  tab,
  page,
  search = '',
  includeRequestedTransaction = false,
}) => {
  const { transactions, pagination } = await queryTransactionsPage(
    dispatch,
    sdk,
    createTransactionQueryParams({ tab, page })
  );

  if (!includeRequestedTransaction) {
    dispatch(fetchOrdersOrSalesSuccess(transactions, pagination, page));
    return { transactions, pagination };
  }

  const requestedTransactionId = getRequestedTransactionId(search);
  const hasRequestedTransaction = transactions.some(tx => tx?.id?.uuid === requestedTransactionId);
  let transactionsToStore = transactions;

  if (
    requestedTransactionId &&
    !isBlockedTransactionId(requestedTransactionId) &&
    !hasRequestedTransaction
  ) {
    try {
      const requestedTransaction = await fetchTransactionById(
        dispatch,
        sdk,
        requestedTransactionId,
        tab
      );

      if (requestedTransaction?.id) {
        transactionsToStore = mergeUniqueById([requestedTransaction, ...transactions]).slice(
          0,
          TRANSACTION_PAGE_SIZE
        );
      }
    } catch (e) {
      log.error(e, 'messages-page-requested-transaction-fetch-failed', {
        requestedTransactionId,
        tab,
      });
    }
  }

  dispatch(fetchOrdersOrSalesSuccess(transactionsToStore, pagination, page));
  return { transactions: transactionsToStore, pagination };
};

// ================ Thunks ================ //

export const fetchMoreMessages = txId => (dispatch, getState) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages, messageTransactionId } = state.MessagesPage;
  const txUuid = txId?.uuid || txId;

  const isNewThread = messageTransactionId !== txUuid;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;
  const nextPage = isNewThread
    ? 1
    : hasMoreOldMessages
    ? oldestMessagePageFetched + 1
    : oldestMessagePageFetched || 1;

  return dispatch(fetchMessages(txId, nextPage));
};

export const sendMessage = (txId, message, tab) => async (dispatch, getState) => {
  dispatch(sendMessageRequest());

  try {
    const transaction = getLoadedTransaction(getState(), txId);
    const unavailableParticipant = getUnavailableTransactionParticipant(transaction);

    if (unavailableParticipant) {
      const blockedError = createParticipantUnavailableError();
      log.warn(
        blockedError,
        'messages-send-blocked-unavailable-participant',
        unavailableParticipantLogData(transaction, unavailableParticipant)
      );
      blockedError.messageFailureReported = true;
      throw blockedError;
    }

    const normalizedTab = normalizeMessagesTab(tab);
    const isOrderMessage = normalizedTab === TAB_ORDER;
    const response = await sendMarketplaceMessage({
      transactionId: txId,
      content: message,
      // Non-order buyer/seller replies were restored after the bot incident.
      // Server-side account availability, first-message checks, and inquiry rate
      // limits still decide whether the message can actually be sent.
      isOrderMessage,
      context: {
        source: 'MessagesPage',
        tab: normalizedTab,
      },
    });
    const saved = confirmedMessage(response, message, getState().user.currentUser);
    dispatch(sendMessageSuccess(saved, txId?.uuid || txId));
    // Refresh reads cannot turn an acknowledged send into a failure or keep
    // the composer locked. Never retry the mutation automatically.
    dispatch(fetchMessages(txId, 1));
    // Refresh in place: loadData resets the selected thread and unmounts its
    // composer, which can discard a draft or an attachment batch still sending.
    dispatch(refreshConversations(normalizedTab, txId));
    return saved.id;
  } catch (e) {
    const error = storableError(e);
    reportMessageSendFailure(e, txId, 'MessagesPage');
    dispatch(sendMessageError(error));
    throw error;
  }
};

const refreshConversations = (tab, txId) => async (dispatch, getState, sdk) => {
  const snapshot = getState().MessagesPage;
  const page = snapshot.currentTransactionPage || 1;
  const sentTransaction = getLoadedTransaction(getState(), txId);
  try {
    const { transactions, pagination, hiddenTransactionIds } = await queryTransactionsPage(
      dispatch, sdk, createTransactionQueryParams({ tab, page })
    );
    const current = getState().MessagesPage;
    if (current.transactionRefs !== snapshot.transactionRefs || current.currentTransactionPage !== page) {
      return;
    }
    // Sending from a later page can move this thread onto page one on the
    // server. Keep it mounted while the user completes their attachment batch.
    const txUuid = txId?.uuid || txId;
    const keepSentThread = sentTransaction &&
      !hiddenTransactionIds.includes(txUuid) &&
      !transactions.some(tx => tx.id.uuid === txUuid);
    const refreshed = keepSentThread
      ? [sentTransaction, ...transactions].slice(0, TRANSACTION_PAGE_SIZE)
      : transactions;
    dispatch(fetchOrdersOrSalesSuccess(refreshed, pagination, page));
  } catch (_error) {
    // The acknowledged message is already visible. A list refresh failure must
    // not be presented as a failed send or invite a duplicate retry.
  }
};

export const uploadImageAttachment = file => async (dispatch, getState, sdk) => {
  try {
    const response = await sdk.images.upload(
      {
        image: file,
      },
      {
        expand: true,
      }
    );

    return response?.data?.data?.attributes?.variants?.default?.url || NO_IMAGE;
  } catch (e) {
    log.error(e, 'messages-image-upload-failed', {
      fileSize: file?.size,
      fileType: file?.type,
    });
    throw storableError(e);
  }
};

export const updateOffer = ({ id, transition, message = '', offerParams = null }) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(updateOfferProgress());

  const txId = id?.uuid || id;
  const messageContent = typeof message === 'string' ? message.trim() : '';
  const transitionParams = offerParams
    ? {
        protectedData: {
          offerParams,
        },
      }
    : {};

  try {
    const response = await guardedTransaction({
      action: 'transition',
      bodyParams: { id, transition, params: transitionParams },
      queryParams: { expand: true, ...transactionOnlyApiQueryParams },
    });
    dispatch(addMarketplaceEntities(response));

    triggerTransactionSmsNotifications({
      transactionId: id,
      source: 'MessagesPage',
      transition,
    });

    if (messageContent) {
      await sendMarketplaceMessage({
        transactionId: id,
        content: messageContent,
        isOrderMessage: false,
        isOfferMessage: true,
        context: {
          source: 'MessagesPage',
          transition,
          transactionId: txId,
          hasOffer: true,
          isOfferMessage: true,
          offerType: offerParams?.type || null,
        },
      });
    }

    dispatch(updateOfferSuccess());
    return response?.data?.data || null;
  } catch (e) {
    const error = storableError(e);
    log.error(e, 'messages-offer-update-failed', {
      txId,
      transition,
      hasMessage: Boolean(messageContent),
      offerType: offerParams?.type || null,
      errorStatus: e?.status || e?.statusCode || null,
      errorCode: e?.code || null,
      errorMessage: e?.message || e?.statusText || null,
    });
    dispatch(updateOfferError(error));
    throw error;
  }
};

export const loadData = (params, search = '') => async (dispatch, getState, sdk) => {
  const tab = normalizeMessagesTab(params?.tab);
  dispatch(fetchOrdersOrSalesRequest({ page: 1, reset: true, isPageChange: false }));

  try {
    await fetchTransactionPage({
      dispatch,
      sdk,
      tab,
      page: 1,
      search,
      includeRequestedTransaction: true,
    });
  } catch (e) {
    const error = storableError(e);
    dispatch(fetchOrdersOrSalesError(error));
  }
};

export const changeTransactionPage = ({ page, tab }) => async (dispatch, getState, sdk) => {
  const requestedPage = Number(page || 1);
  const normalizedTab = normalizeMessagesTab(tab);
  const state = getState()?.MessagesPage || {};
  const {
    currentTransactionPage,
    fetchInProgress,
    transactionPageFetchInProgress,
    transactionPageRefs = {},
    pagination,
  } = state;

  const totalPages = Number(pagination?.totalPages || 0);
  const hasCachedPage = Array.isArray(transactionPageRefs[requestedPage]);

  if (
    requestedPage < 1 ||
    requestedPage === currentTransactionPage ||
    fetchInProgress ||
    transactionPageFetchInProgress ||
    (totalPages > 0 && requestedPage > totalPages)
  ) {
    return null;
  }

  if (hasCachedPage) {
    dispatch(setTransactionPage(requestedPage));
    return requestedPage;
  }

  dispatch(fetchOrdersOrSalesRequest({ page: requestedPage, reset: false, isPageChange: true }));

  try {
    await fetchTransactionPage({
      dispatch,
      sdk,
      tab: normalizedTab,
      page: requestedPage,
      includeRequestedTransaction: false,
    });
    return requestedPage;
  } catch (e) {
    const error = storableError(e);
    dispatch(fetchOrdersOrSalesError(error));
  }
};
